/**
 * Terrain types available in the game world
 */
export enum TerrainType {
  Mountain = 0,
  Tundra = 1,
  Hills = 2,
  Forest = 3,
  Settlement = 4,
  River = 5,
  Desert = 6,
  Ocean = 7,
}

/**
 * Axial coordinate system for hexagons
 * q = column, r = row
 */
export interface AxialCoord {
  q: number;
  r: number;
}

/**
 * Cube coordinate system for hexagons
 * x + y + z = 0 (constraint)
 */
export interface CubeCoord {
  x: number;
  y: number;
  z: number;
}

/**
 * Pixel coordinates for rendering
 */
export interface PixelCoord {
  x: number;
  y: number;
}

/**
 * Faction identifiers
 */
export type FactionId = 
  | 'highland-clans'
  | 'stoneheart-clans'
  | 'sea-kings'
  | 'hearth-folk'
  | 'wildwood-folk'
  | 'riverbend-kingdoms'
  | null; // wilderness

/**
 * Individual hex data structure
 */
export interface TerrainHex {
  q: number;           // Axial coordinate column
  r: number;           // Axial coordinate row
  terrain: TerrainType; // Type of terrain
  elevation: number;    // 0-255 elevation value
  biome?: string;      // Optional biome classification
  regionId?: number;   // For named regions
  faction?: FactionId;  // Faction territory (null = wilderness)
  playedBy?: string[]; // Player IDs who have interacted with this hex (quests no longer available)
  campaign?: string | null; // Campaign ID if a campaign exists here (null = no campaign)
}

/**
 * Complete map data structure
 */
export interface TerrainMap {
  width: number;       // Map width in hexes
  height: number;      // Map height in hexes
  hexes: Map<string, TerrainHex>; // Hex data keyed by "q,r"
  seed: string;        // Seed used for generation
  startPosition?: AxialCoord; // Optional starting position on land
}

/**
 * Region data for macro map rendering
 */
export interface MapRegion {
  terrainType: TerrainType;
  hexes: AxialCoord[];
  boundaryPoints: PixelCoord[];
  centerPoint: PixelCoord;
  name?: string;
  size: number;
}

/**
 * Player exploration state
 */
export interface ExplorationState {
  playerId: string;
  exploredHexes: Set<string>; // "q,r" format
  currentPosition: AxialCoord;
  discoveredRegions: string[];
  visitedSettlements: string[];
}

/**
 * Terrain color mapping for rendering
 */
export const TERRAIN_COLORS: Record<TerrainType, string> = {
  [TerrainType.Mountain]: '#666666',     // Dark gray (more distinct)
  [TerrainType.Tundra]: '#848c24',       // Grassland green
  [TerrainType.Hills]: '#c1ba3f',        // Yellow-green
  [TerrainType.Forest]: '#697c2c',       // Forest green
  [TerrainType.Settlement]: '#8a6723',   // Brown
  [TerrainType.River]: '#539aac',        // Blue
  [TerrainType.Desert]: '#ba8f55',       // Sand/tan
  [TerrainType.Ocean]: '#1e3e5e',        // Deep blue ocean (slightly lighter than background for visibility)
};

/**
 * Terrain names for display
 */
export const TERRAIN_NAMES: Record<TerrainType, string> = {
  [TerrainType.Mountain]: 'Mountain',
  [TerrainType.Tundra]: 'Grassland',  // Tundra displays as grassland
  [TerrainType.Hills]: 'Hills',
  [TerrainType.Forest]: 'Forest',
  [TerrainType.Settlement]: 'Settlement',
  [TerrainType.River]: 'River',
  [TerrainType.Desert]: 'Desert',
  [TerrainType.Ocean]: 'Ocean',
};

